/**
 * The Delete-a-portal-user dialog. One modal, two faces:
 *
 *  • REFUSED — a guardrail applies (self / an Admin aiming at an Owner / still active / the last active
 *    Owner). We say WHICH ONE, in a sentence, naming the person. There is no destructive button on this
 *    face at all: the action isn't merely disabled, it isn't offered. A greyed-out control with no
 *    explanation is how an operator concludes the portal is broken — so the control stays reachable and
 *    the click buys you the reason.
 *
 *  • ALLOWED — the consequence, in the principal's own words ("they go back to being just a Zoom-roster
 *    entry; press Invite to bring them back"), the person's EMAIL spelled out (never a bare "Are you
 *    sure?" — you must be able to see WHO you are about to remove), and a danger button.
 *
 * The app's own Modal, never window.confirm(): a native dialog can't be styled, can't be translated,
 * can't name the row it came from, and can't be tested.
 *
 * The reason shown here is a PRE-FLIGHT, not the gate — deleteGuards mirrors the server's rules and its
 * order, but the list is a snapshot. If the server disagrees on the click (someone re-enabled the user
 * in another tab), its 4xx message is rendered right here, in place, verbatim.
 */

import { useTranslation } from "react-i18next";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { Button, Modal } from "@/components/ui";
import type { AdminUser, Me } from "@/api/types";
import type { TKey } from "@/i18n/keys";
import { deleteRefusal, type DeleteRefusal } from "./deleteGuards";

const REFUSAL_COPY = {
  self: "adminUsers.deleteBlocked.self",
  owner_needs_owner: "adminUsers.deleteBlocked.ownerNeedsOwner",
  still_active: "adminUsers.deleteBlocked.stillActive",
  last_owner: "adminUsers.deleteBlocked.lastOwner",
} as const satisfies Record<DeleteRefusal, TKey>;

/** The way OUT of each refusal. Spelled out, not built by string-concatenating "Fix" onto the key
 *  above: a mistyped dynamic key doesn't fail — it renders the key itself at the operator, which is
 *  how "the portal is broken" happens.
 *
 *  That warning is now enforced rather than merely observed: `satisfies Record<DeleteRefusal, TKey>`
 *  checks every value against the real resource tree. (The old `: Record<DeleteRefusal, string>`
 *  annotation actively DEFEATED the check — it widened each key literal to `string`, so neither the map
 *  nor the `t()` call downstream could see the typo the comment is about.) */
const REFUSAL_FIX = {
  self: "adminUsers.deleteBlocked.selfFix",
  owner_needs_owner: "adminUsers.deleteBlocked.ownerNeedsOwnerFix",
  still_active: "adminUsers.deleteBlocked.stillActiveFix",
  last_owner: "adminUsers.deleteBlocked.lastOwnerFix",
} as const satisfies Record<DeleteRefusal, TKey>;

interface DeleteUserModalProps {
  target: AdminUser;
  actor: Me;
  /** The full list — the last-active-Owner count is a property of the SET, not of the row. */
  users: AdminUser[];
  pending: boolean;
  /** The server's verbatim refusal, if the click lost a race with reality. */
  serverError: string | null;
  onClose: () => void;
  onConfirm: (userId: string) => void;
}

export function DeleteUserModal({
  target,
  actor,
  users,
  pending,
  serverError,
  onClose,
  onConfirm,
}: DeleteUserModalProps) {
  const { t } = useTranslation();
  const refusal = deleteRefusal(target, actor, users);

  if (refusal) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t("adminUsers.deleteBlockedTitle")}
        size="sm"
        footer={
          <Button variant="secondary" onClick={onClose}>
            {t("action.close")}
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div
            role="alert"
            className="flex items-start gap-2 rounded-(--radius) border border-(--warn-subtle) bg-(--warn-subtle) p-3"
          >
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-(--warn)" aria-hidden />
            <p className="text-(length:--text-sm) text-(--text)">
              {t(REFUSAL_COPY[refusal], { email: target.email })}
            </p>
          </div>
          {/* The way OUT of the refusal, not just the refusal. */}
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {t(REFUSAL_FIX[refusal], { email: target.email })}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("adminUsers.deleteDialogTitle")}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            loading={pending}
            onClick={() => onConfirm(target.user_id as string)}
          >
            {t("adminUsers.deleteConfirm")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Names the person. A confirmation that doesn't say WHO is not a confirmation. */}
        <p className="text-(length:--text-sm) text-(--text)">
          {t("adminUsers.deleteDialogBody", { email: target.email })}
        </p>
        <p className="text-(length:--text-sm) text-(--text-muted)">
          {t("adminUsers.deleteDialogConsequence")}
        </p>
        {serverError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-(--radius) border border-(--danger-subtle) bg-(--danger-subtle) p-3"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-(--danger)" aria-hidden />
            <p className="text-(length:--text-sm) text-(--danger-strong-fg)">{serverError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
